import { useTranslation } from 'react-i18next';
import { BaseDiv, Button, Card, CtaButtonBar, Heading2, IGapProps, P, useColumnGapCss, useGapCss } from '@cmctechnology/webinvest-design-system';
import { styled } from 'styled-components';
import { useSelector } from 'react-redux';
import { IStore } from '../store/store';
import { useFrontendContext } from '@cmctechnology/webinvest-store-frontend';
import { cfdPlatformThemes, proPlatformThemes, THEMES } from '@cmctechnology/webinvest-constants';
import { ClientRoles, useClientProSubscriptionInfo } from '@cmctechnology/webinvest-store-client';
import { useNavigate } from 'react-router';
import { ProExpiredModal } from '../Modules';
import { useState } from 'react';

const CFDPlatformImage = 'https://cmcmarketsinvest.com/webinvest-module-icons/v/latest/images/r/390/CFD-platform.png';

const ProPlatformImage = 'https://cmcmarketsinvest.com/webinvest-module-icons/v/latest/images/r/390/PRO-platform.png';

const CardContainer = styled(BaseDiv)`
  width: 22rem;
`;

const PageContainer = styled(BaseDiv)`
  padding-top: 1.4rem;
  padding-left: 2rem;
  margin-bottom: 2rem;
  height: auto;
`;

const ColsContainer = styled(BaseDiv)<IGapProps>`
  ${useColumnGapCss()}
`;

const MultiContainer = styled(BaseDiv)<IGapProps>`
  ${useGapCss()}
`;

const cfdAuLearnMoreUrl = 'https://www.cmcmarkets.com/en-au/cfd';
const cfdSgLearnMoreUrl = 'https://www.cmcmarkets.com/en-sg/cfds';

// PRO platform is not applicable to singapore
const proAuLearnMoreUrl = 'https://www.cmcmarkets.com/en-au/stockbroking/platform/pro-platform';
const CMC_SGP_CFD_URL = 'https://platform.cmcmarkets.com/#/login?b=CMC-CFD&r=SG&l=en';
const CMC_AU_CFD_URL = 'https://platform.cmcmarkets.com/#/login?b=CMC-CFD&r=AU&l=en';

export const Platforms = () => {
  const { t } = useTranslation();
  const { tradingRoutePrefix, config, theme } = useFrontendContext();
  const navigate = useNavigate();
  const subscriptionInfo = useClientProSubscriptionInfo();
  const [showProExpiredModal, setShowProExpiredModal] = useState(false);

  const roles = useSelector((store: IStore) => store.client.roles);
  const accessProEnabled = roles.includes(ClientRoles.ACCESS_PRO);

  const isCfdEnabledTheme = cfdPlatformThemes.includes(theme);
  const isProEnabledTheme = proPlatformThemes.includes(theme);
  const hasNextGenSsoRole = roles.includes(ClientRoles.NEXT_GEN_SSO_ENABLED);
  const nonNextGenSsoCfdLink = theme === THEMES.CMCSingapore ? CMC_SGP_CFD_URL : CMC_AU_CFD_URL;
  const clientProSubscriptionInfo = subscriptionInfo?.clientProSubscriptionInfo;
  const proExpiryDate = clientProSubscriptionInfo?.EndDate;
  /* istanbul ignore next */
  const isProSubscriptionExpired = !!proExpiryDate && new Date(proExpiryDate) < new Date();

  return (
    <PageContainer>
      <Heading2 headingType='SEMI_BOLD'>{t(`Platforms`)}</Heading2>
      <ColsContainer>
        <MultiContainer>
          {isProEnabledTheme && (
            <CardContainer>
              <Card
                variant='accent'
                title={t('Pro platform')}
                body={
                  <P gutterBottom={false}>
                    {t('Take your CMC invest account to the next level with customisable layouts, advanced charting and ASX options trading.')}
                  </P>
                }
                ctaButtonBar={
                  <CtaButtonBar
                    ctaFullOnMobile={false}
                    ctaButtons={
                      <>
                        {accessProEnabled ? (
                          <>
                            <Button
                              variant='primary'
                              onClick={() => {
                                /* istanbul ignore next */
                                if (isProSubscriptionExpired) {
                                  setShowProExpiredModal(true);
                                  return;
                                }
                                window.open(config.proUrl);
                              }}
                            >
                              {t('Login')}
                            </Button>
                            <Button
                              variant='secondary'
                              onClick={() => {
                                navigate(`pro/unsubscribe`);
                              }}
                            >
                              {t('Unsubscribe from Pro')}
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button variant='primary' onClick={() => navigate(`pro/subscription`)}>
                              {t('Subscribe to Pro')}
                            </Button>
                            <Button
                              variant='link'
                              onClick={() => {
                                window.open(proAuLearnMoreUrl);
                              }}
                            >
                              {t('Learn more')}
                            </Button>
                          </>
                        )}
                      </>
                    }
                  />
                }
                image={ProPlatformImage}
              />
            </CardContainer>
          )}
          {isCfdEnabledTheme && (
            <CardContainer>
              <Card
                variant='accent'
                title={t('CFD platform')}
                body={
                  <P gutterBottom={false}>
                    {t('Access instruments with competitive spreads and trade CFDs on forex, indices, commodities, shares and treasuries.')}
                  </P>
                }
                image={CFDPlatformImage}
                ctaButtonBar={
                  <CtaButtonBar
                    ctaFullOnMobile={false}
                    ctaButtons={
                      <>
                        <Button
                          variant='primary'
                          onClick={() => {
                            if (hasNextGenSsoRole) {
                              window.open(`${tradingRoutePrefix}/NextGenSso`);
                              return;
                            }
                            window.open(nonNextGenSsoCfdLink);
                          }}
                        >
                          {t('Login')}
                        </Button>
                        <Button
                          onClick={() => {
                            if (theme === THEMES.CMCSingapore) {
                              window.open(cfdSgLearnMoreUrl);
                              return;
                            }
                            window.open(cfdAuLearnMoreUrl);
                          }}
                          variant='link'
                        >
                          {t('Learn more')}
                        </Button>
                      </>
                    }
                  />
                }
              />
            </CardContainer>
          )}
        </MultiContainer>
      </ColsContainer>
      <ProExpiredModal loadingElement={<></>} showModal={showProExpiredModal} onClose={/* istanbul ignore next */ () => setShowProExpiredModal(false)} />
    </PageContainer>
  );
};
